
import React from 'react'
import { Link } from 'react-router-dom'

import User from '../../models/User'

import Page from '../Page'
import EmptyBanner from '../../components/EmptyBanner'
import ErrorBanner from '../../components/ErrorBanner'
import LoadingBanner from '../../components/LoadingBanner'
import NavTabs from '../../components/nav/NavTabs'
import NavBar from '../../components/nav/NavBar'

import InviteUserPage from '../users/Invite'
import EditUserPage from '../users/Edit'
import InfoTooltip from "../../components/InfoTooltip";
import {useLocation, useNavigate, useParams} from "react-router-dom";
import UserImageCircle from "../../components/UserImageCircle";

export default class ClientUsersList extends Page {

    /*
    |--------------------------------------------------------------------------
    | Paths w/ User Statuses
    |--------------------------------------------------------------------------
    */

	static path = '/client/org/users/:status';

	static defaultPath() {
		if (!laravel.client_user_statuses) {
			laravel.client_user_statuses = [
			    'Active', 'Unregistered', 'Invited'
            ];
		}
		return this.pathFor(
			'active'
		);
	}

	static pathFor(status) {
		return TB.pathFrom(
			this.path, { status: TB.slugify(status) }
		);
	}

	static getDerivedStateFromProps(props, state) {
		let activeTab = TB.titleCase(
			props.status.replace('-', ' ')
		);
		if (!laravel.client_user_statuses) {
			laravel.client_user_statuses = [];
		}
		if (!laravel.client_user_statuses.includes(activeTab)) {
			props.navigate(
				ClientUsersList.defaultPath()
			, {replace: true});
			return null;
		}

		let newState = { ...state };
		newState.activeTab = activeTab;

		return newState;
	}

	constructor(props) {
		super(props);

		this.state = {
			...this.state,
			isLoading: true,
			error: null,
		};
	}

	async componentDidMount() {
		super.componentDidMount();

		await this.fetchUsers();
	}

    /*
    |--------------------------------------------------------------------------
    | Refreshing/viewing state users
    |--------------------------------------------------------------------------
    */

	static handle = {
		'Users@updated': (component) => component.fetchUsers(),
	};

	async fetchUsers() {
		this.promisedState({
			users: undefined,
			isLoading: true,
			error: null,
		});

		let users = await User.fetchAllForClient({}, `?type=${laravel.user_types.client}`);
		if (!users) {
			return this.handleErrorResponse(
				User.FetchAllForClientRequest, 'error_index_client_users'
			);
		}

		if (!this._isMounted) {
			return;
		}

		// let groupedUsers = TB.groupBy(users, 'status');

		await this.setState({
			users,
			error: null,
		});

		await this.refreshTabsForUsers();

		this.setState({
			isLoading: false,
		});
	}

	statusFromSlug(statusSlug)
	{
		return TB.titleCase(statusSlug.replace('-', ' '))
	}

	viewUsersWithStatus(tab)
	{
		this.props.navigate({
			pathname: this.constructor.pathFor(
				TB.slugify(tab.title)
			)
		});
	}

	usersWithStatus(status) {
		if (!TB.isArray(this.state.users)) {
			return;
		}
		return TB.filter([...this.state.users], ['status', status]) || [];
	}

	async refreshTabsForUsers() {

		this.tabs = TB.map([...laravel.client_user_statuses], status => {
			let users = this.usersWithStatus(status);
			return {
				title: status,
				counts: users ? users.length : 0,
			};
		});
	}

	async refreshUsersFor(updatedUser) {
        if (!TB.isArray(this.state.users)) {
            return;
        }

        if (updatedUser) {
	        let users = [...this.state.users];

	        users = TB.set(users, TB.findIndex([...this.state.users], ['id', updatedUser.id]), updatedUser);

			await this.promisedState({users});
		}

		await this.refreshTabsForUsers();
	}

    /*
    |--------------------------------------------------------------------------
    | Post Requests - Reactivate/Deactivate/Resend Invite
    |--------------------------------------------------------------------------
    */

	async confirmDeactivate(user)
	{
        let didConfirm = await TB.sysAlert('client.confirm_deactivate_user',  {
            name: user.fullName()
        })

		if (!didConfirm) {
			return;
		}

		await this.promisedState({
			isDisabled: true,
		});

		let updatedUser = await user.deactivate();

		if (!updatedUser) {
		    await this.promisedState({
                isDisabled: false,
            });

            User.DeactivateForClientRequest.didHandleError = await User.DeactivateForClientRequest.handleError();
            if (User.DeactivateForClientRequest.didHandleError
            	&& User.DeactivateForClientRequest.shouldRerun) {
                return this.confirmDeactivate(
                    user
                );
            }
		}

		await this.refreshUsersFor(updatedUser);

		await this.promisedState({
			selectedUser: undefined,
			isDisabled: false,
		});

        return TB.sysAlert('client.success_feedback_deactivated_user')
	}

	async restore(user) {
		// console.log('Users @ restore', user);

		await this.promisedState({
			isDisabled: true,
		});

		let updatedUser = await user.restore();
		if (!updatedUser) {
			// handle error
            await this.promisedState({
                isDisabled: false,
            });

            User.RestoreForClientRequest.didHandleError = await User.RestoreForClientRequest.handleError();
            if (User.RestoreForClientRequest.didHandleError
            	&& User.RestoreForClientRequest.shouldRerun) {
                return this.restore(
                    user
                );
            }
		}

		await this.refreshUsersFor(updatedUser);

		await this.promisedState({
			isDisabled: false,
		});


        return TB.sysAlert('client.success_feedback_reactivated_user')
    }

	async resendInvite(user) {
		// console.log('Users @ resendInvite', user);

		await this.promisedState({
			isDisabled: true,
		});

		let updatedUser = await user.resendInvite();

		if (!updatedUser) {
            User.ResendInviteForClientRequest.didHandleError = await User.ResendInviteForClientRequest.handleError();

            if (User.ResendInviteForClientRequest.didHandleError && User.ResendInviteForClientRequest.shouldRerun) {
                return this.resendInvite(
                    user
                );
            }

			// handle error
			return this.promisedState({
				isDisabled: false,
			});
		}

		await this.promisedState({
			isDisabled: false,
		});


        return TB.sysAlert('client.success_feedback_resend_invite')
	}

    /*
    |--------------------------------------------------------------------------
    | Render Components
    |--------------------------------------------------------------------------
    */

	render() {
		let auth = this.context?.auth;

		if (!auth) {
			return <div/>;
		}

		let client = auth.clientOrg();

		let activeStatus = this.statusFromSlug(this.props.status),
			activeUsers = this.usersWithStatus(activeStatus),
			activeTab = {
				title: activeStatus,
				counts: activeUsers ? activeUsers.length : 0
			};

		return (
			<div className='container-fluid'>

				<NavBar title="Client Users"
					stats={[
						{
							title: 'clients.company_users.subtitle_label',
							value: client?.total_users
						}
					]}
					button={{
						path: '/client' + InviteUserPage.path,
						icon: 'plus',
						title: 'clients.company_users.invite_btn',
					}} />

				{this.state.isLoading && <LoadingBanner message="clients.company_users.loading" />}

				{TB.isObject(this.state.error) && (
					<ErrorBanner {...this.state.error}
						onRetry={this.fetchUsers.bind(this)} />
				)}

				{!this.state.isLoading && !TB.isObject(this.state.error) && (
					<div>
						<NavTabs
							tabs={this.tabs}
							active={activeTab}
							onChange={this.viewUsersWithStatus.bind(this)}
						/>

						<div className="m-0 p-4 md:p-8">
							{this.renderUsers()}
						</div>
					</div>
				)}
			</div>
		);
	}

	renderUsers() {
		if (!this.state.users) {
			return <EmptyBanner message={TB.trans('clients.company_users.not_found', {user_status: ''})} />
		}

		let status = this.statusFromSlug(this.props.status),
			users = this.usersWithStatus(status);
		if (!users || !users.length) {
			return <EmptyBanner message={TB.trans('clients.company_users.not_found', {user_status: status})} />
		}

		let auth = this.context?.auth;

		return (
			<div className="p-4 bg-white font-primary">
				<div className="table-responsive w-full overflow-auto">
					<table className="table-auto users-table w-full" style={{ minWidth: '950px' }}>
						<thead className="border-b border-gray-300">
							<tr className="text-left">
								<th/>

                                <th>Status</th>
								<th>{TB.trans('clients.company_users.table_col_contact')}</th>
                                {(auth.isClientAdmin() || auth.isClientConsultant()) && (
                                    <th>Role</th>
                                )}
								<th>{TB.trans('clients.company_users.table_col_registered')}</th>

                                {(auth.isClientAdmin() || auth.isClientConsultant()) && (
									<th/>
								)}
							</tr>
						</thead>
						<tbody>
							{users.map((user, i) => {
                                const isAuth = user.id === auth.id;
								/*let canManage = auth.isClientOwner()
									&& !user.isClientOwner();
								if (!canManage) {
									canManage = auth.isClientSuperAdmin()
										&& (user.isClientBaseUser() || user.isClientAdmin());
								}
                                if (!canManage) {
                                    canManage = auth.isClientAdmin()
                                        && user.isClientBaseUser();
                                }
                                if (!canManage && auth.isClientConsultant()) {
                                    canManage = true
                                }

                                if (isAuth) {
                                    canManage = false;
                                }*/

								return (
									<tr key={`userRow${i}`} className="border-b border-gray-200">
										<td className="user-info">
											<div>

												<UserImageCircle
													user={user}
													hideTooltip={true}
													size={80} className="img-fluid user-image-80 img-grayscale"/>
												<p>
													<span className="user-name">{user.fullName()} {isAuth && (
                                                        <i className="text-danger">(You)</i>
                                                    )}</span><br />
													<span className="user-job-title">{user.job_title}</span>
												</p>
											</div>
										</td>

										<td style={styles.tableCell}>
                                            {user.status}
										</td>
										<td style={styles.tableCell}>
											<a className="user-email" href={`mailto:${user.email}`}>{user.email}</a>
											<br />
											{TB.isString(user.phone) && (
												<a className="user-phone" href={`tel:${user.phone.replace(/^\D+/g, '')}`}>{user.phone}</a>
											)}
										</td>
                                        {(auth.isClientAdmin() || auth.isClientConsultant()) && (
                                            <td style={styles.tableCell}>
                                                {user.role_name}
                                            </td>
                                        )}

										<td style={styles.tableCell}>
											{user.added_at ? TB.moment(user.added_at).format('MM/DD/YYYY') : '---'}
										</td>

										{(auth.isClientAdmin() || auth.isClientConsultant()) && (
											<td className="whitespace-nowrap">
												{user.can_be_invited && !user.isRegistered() && !user.isDeactivated() && (
                                                    <div className="inline-flex flex-col mr-2">
                                                        <button className="btn btn-dark table-btn"
                                                                id={`invite-user-btn-${user.id}`}
                                                                onClick={() => this.resendInvite(user)}
                                                                disabled={this.state.isDisabled}>
                                                            <i className="fa fa-envelope"/>
                                                        </button>
                                                        <InfoTooltip
                                                            type="dark"
                                                            target={`invite-user-btn-${user.id}`}>
                                                            Resend Invite
                                                        </InfoTooltip>
                                                    </div>
												)}

												{user.can_be_managed && !user.isDeactivated() && (
	                                                <div className="inline-flex flex-col">
	                                                    <Link to={'/client' + EditUserPage.path.replace(':userId', user.id)}
	                                                          id={`edit-user-btn-${user.id}`}
	                                                          className="btn btn-primary table-btn"
	                                                          disabled={this.state.isDisabled}>
	                                                        <i className="fa fa-edit"/>
	                                                    </Link>
	                                                    <InfoTooltip
	                                                        type="primary"
	                                                        target={`edit-user-btn-${user.id}`}>
	                                                        Edit User
	                                                    </InfoTooltip>
	                                                </div>
	                                            )}

												{user.can_be_managed && !user.isDeactivated() && (
                                                    <div className="inline-flex flex-col ml-2">
                                                        <button className="btn bg-danger text-white table-btn"
                                                                id={`deactivate-user-btn-${user.id}`}
                                                                onClick={() => this.confirmDeactivate(user)}
                                                                disabled={this.state.isDisabled}>
                                                            <i className="fa fa-times"/>
                                                        </button>
                                                        <InfoTooltip
                                                            type="danger"
                                                            target={`deactivate-user-btn-${user.id}`}>
                                                            Deactivate User
                                                        </InfoTooltip>
                                                    </div>
												)}
												{user.can_be_managed && user.isDeactivated() && (
													<div className="inline-flex flex-col ml-2">
                                                        <button className="btn btn-warning table-btn"
                                                                id={`restore-user-btn-${user.id}`}
                                                                onClick={() => this.restore(user)}
                                                                disabled={this.state.isDisabled}>
                                                            <i className="fa fa-user-plus"/>
                                                        </button>
                                                        <InfoTooltip
                                                            type="warning"
                                                            target={`restore-user-btn-${user.id}`}>
                                                            Restore User
                                                        </InfoTooltip>
                                                    </div>
												)}
											</td>
										)}
									</tr>
								);
							})}

						</tbody>
					</table>
				</div>
			</div>
		);
	}
}

const ClientMembersList= (props) => {
	const {status = 'active'} = useParams();
	const location = useLocation();
	const navigate = useNavigate();

	return (
		<ClientUsersList status={status} navigate={navigate} location={location}/>
	)
}

export {
	ClientMembersList
}
const styles = {
	navBar: {
		background: '#323C47',
		alignItems: 'center',
	},
	tableCell: {
		fontSize: '12px',
	},
	tableBtn: {
		width: 'auto',
		fontSize: 11,
		paddingLeft: 12,
		paddingRight: 12,
	}
};